import { Table, Header, Body, Cell, Row, showToast } from "@jobber/components";
import { Button } from "@jobber/components/Button";
import React, { useRef } from "react";
import { trpc } from "@/services/trpc";
import CreateServiceAreaModal from "@/components/CreateServiceAreaModal";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import type { RefFields as CreateServiceAreaRefFields} from "@/components/CreateServiceAreaModal";
import type { RefFields as ConfirmRefFields } from "@/components/ConfirmationDialog";
import { ServiceAreaUseQueryType } from "@/services/trpc/types";

export default function ServiceAreasSettingsTab() {

  const serviceAreasQuery = trpc.serviceAreas.list.useQuery() as ServiceAreaUseQueryType
  const deleteServiceAreaMutation = trpc.serviceAreas.delete.useMutation()
  const seedServiceAreaMutation = trpc.serviceAreas.seed.useMutation()
  const createServiceAreaModalRef = useRef<CreateServiceAreaRefFields>(null)
  const confirmationDialogRef = useRef<ConfirmRefFields>(null)

  const onSeedServiceAreasClick = async () => {
    seedServiceAreaMutation.mutate(undefined, {
      onSuccess: () => {
        showToast({
          message: 'Service areas seeded successfully',
          variation: 'success',
        })
        serviceAreasQuery.refetch()
      },
      onError: (e) => {
        console.log(e.message)
        showToast({
          message: e.message,
          variation: 'error'
        })
      }
    })
  }

  const onCreateClick = () => {
    createServiceAreaModalRef?.current?.openModal()
  }

  const editServiceArea = (serviceArea: any) => {
    createServiceAreaModalRef?.current?.openModal(serviceArea)
  }

  const deleteServiceArea = async (serviceArea: any) => {
    const res = await confirmationDialogRef.current?.open({
      title: `Delete Service Area ${serviceArea.name}`,
      message: 'Are you sure you want to delete this service area?'
    })
    if (res) {
      deleteServiceAreaMutation.mutate(serviceArea.id, {
        onSuccess: () => {
          serviceAreasQuery.refetch()
        }
      })
    }
  }

  const areas = serviceAreasQuery?.data?.map((sa) => (
    <Row key={sa.id as React.Key}>
      <Cell>{sa.name}</Cell>
      <Cell>{sa.technician?.fullName}</Cell>
      <Cell>{sa.address}</Cell>
      <Cell>
        <div style={{ display: "flex", justifyContent: "end" }}>
          <Button
            type="tertiary"
            variation="destructive"
            ariaLabel="delete"
            icon="trash"
            onClick={() => deleteServiceArea(sa)}
          />
          <Button
            type="tertiary"
            variation="subtle"
            ariaLabel="delete"
            icon="edit"
            onClick={() => editServiceArea(sa)}
          />
        </div>
      </Cell>
    </Row>
  )) || <Row><></></Row>


  return (
    <div>
      <div style={{display: 'flex', justifyContent: 'end', marginBottom: '1em'}}>
        <Button label={'Add new'} icon={'plus'} onClick={onCreateClick}/>
      </div>
      {!!serviceAreasQuery?.data?.length ? (
        <Table>
          <Header>
            <Cell>Name</Cell>
            <Cell>Technician</Cell>
            <Cell>Address</Cell>
            <Cell align="right"></Cell>
          </Header>
          <Body>
            {areas}
          </Body>
        </Table>
      ) : (
        <div style={{textAlign: 'center'}}>
          <p>Service areas not found</p>
          <Button
              type="tertiary"
              variation="subtle"
              ariaLabel="Seed service areas"
              icon="task"
              label="Seed service areas"
              onClick={() => onSeedServiceAreasClick()}
            />
        </div>
      )}
      <CreateServiceAreaModal refetchList={serviceAreasQuery.refetch} ref={createServiceAreaModalRef} />
      <ConfirmationDialog ref={confirmationDialogRef} />
    </div>
  )
}
